<script setup lang="ts">
    import { computed } from 'vue';
    import { useDateFormat } from '@/composables/useDateFormat';
    import DatePicker from '@/components/ui/form/DatePicker.vue';
    import { CalendarDate } from '@internationalized/date';
    import { useI18n } from 'vue-i18n';
    import { Label } from '@/components/ui/label';
    import type { Column } from '@tanstack/vue-table';

    interface Props {
        column: Column<any, unknown>;
        modelValue?: any;
    }

    interface Emits {
        (e: 'update:modelValue', value: string | undefined): void;
    }

    const props = defineProps<Props>();
    const emit = defineEmits<Emits>();
    const { t } = useI18n();

    const { parseDateString } = useDateFormat();

    const normalizeDateFormat = (dateStr: string): string => {
        return dateStr.replace(/[./]/g, '-');
    };

    const detectDateFormat = (dateStr: string): 'dot' | 'slash' | 'dash' => {
        if (dateStr.includes('.')) return 'dot';
        if (dateStr.includes('/')) return 'slash';
        return 'dash';
    };

    const formatToOriginalFormat = (
        day: number,
        month: number,
        year: number,
        format: 'dot' | 'slash' | 'dash',
    ): string => {
        const dayStr = day.toString().padStart(2, '0');
        const monthStr = month.toString().padStart(2, '0');

        switch (format) {
            case 'dot':
                return `${dayStr}.${monthStr}.${year}`;
            case 'slash':
                return `${dayStr}/${monthStr}/${year}`;
            case 'dash':
                return `${dayStr}-${monthStr}-${year}`;
            default:
                return `${dayStr}.${monthStr}.${year}`;
        }
    };

    const fromDate = computed<string | undefined>({
        get() {
            if (!props.modelValue || typeof props.modelValue !== 'string') {
                return undefined;
            }

            const parts = props.modelValue.includes(',')
                ? props.modelValue.split(',')
                : [props.modelValue];

            if (parts.length >= 1 && parts[0].trim()) {
                const startDateStr = normalizeDateFormat(parts[0].trim());
                const calendarDate = parseDateString(startDateStr);

                if (calendarDate) {
                    return calendarDate.toString();
                }
            }
            return undefined;
        },
        set(value: string | undefined) {
            updateFilterValue(value, toDate.value);
        },
    });

    const toDate = computed<string | undefined>({
        get() {
            if (!props.modelValue || typeof props.modelValue !== 'string') {
                return undefined;
            }

            const parts = props.modelValue.includes(',') ? props.modelValue.split(',') : [];

            if (parts.length === 2 && parts[1].trim()) {
                const endDateStr = normalizeDateFormat(parts[1].trim());
                const calendarDate = parseDateString(endDateStr);

                if (calendarDate) {
                    return calendarDate.toString();
                }
            }
            return undefined;
        },
        set(value: string | undefined) {
            updateFilterValue(fromDate.value, value);
        },
    });

    const updateFilterValue = (from: string | undefined, to: string | undefined) => {
        // If both dates are empty, clear the filter
        if (!from && !to) {
            emit('update:modelValue', undefined);
            return;
        }

        let originalFormat: 'dot' | 'slash' | 'dash' = 'dot';
        if (props.modelValue && typeof props.modelValue === 'string') {
            const firstDateStr = props.modelValue.includes(',')
                ? props.modelValue.split(',')[0].trim()
                : props.modelValue;
            if (firstDateStr) {
                originalFormat = detectDateFormat(firstDateStr);
            }
        }

        const formatToTableFormat = (dateStr: string): string => {
            const calendarDate = parseDateString(dateStr);
            if (calendarDate) {
                return formatToOriginalFormat(
                    calendarDate.day,
                    calendarDate.month,
                    calendarDate.year,
                    originalFormat,
                );
            }
            return dateStr;
        };

        // Handle different combinations of from/to dates
        if (from && to) {
            // Both from and to dates
            const fromFormatted = formatToTableFormat(from);
            const toFormatted = formatToTableFormat(to);
            emit('update:modelValue', `${fromFormatted},${toFormatted}`);
        } else if (from) {
            // Only from date
            const fromFormatted = formatToTableFormat(from);
            emit('update:modelValue', fromFormatted);
        } else if (to) {
            // Only to date
            const toFormatted = formatToTableFormat(to);
            emit('update:modelValue', `,${toFormatted}`);
        }
    };
</script>

<template>
    <div class="flex gap-2">
        <div class="flex flex-1 flex-col gap-2">
            <Label>{{ t('forms.from') }}</Label>
            <DatePicker v-model="fromDate" />
        </div>

        <div class="flex flex-1 flex-col gap-2">
            <Label>{{ t('forms.to') }}</Label>
            <DatePicker v-model="toDate" />
        </div>
    </div>
</template>
